import EditCell from 'components/UI/EditCell';
import { emptyProductAddon, IProductAddonForm, ProductAddonPreview } from 'data/Service';
import React, { useState } from 'react';
import styled from 'styled-components';
import { Main, Shade } from 'styles/themeColors';
import { Space } from 'styles/themeSpaces';
import { currencySign } from 'utils/formatters';
import OrderAddonForm from './OrderAddonForm';
import { useAPI } from 'lib/API';
import { validateProductAddonMapping } from 'requests/consignment';
import { useCountries } from 'hooks/useCountries';
import Icon from 'view/Icon';
import { productAddonPlannedUsageValidator, productAddonQuantityValidator, serialNumberValidator } from 'utils/consignment';
import { FontWeight } from 'lib/styles';

// Styled Components
const TableContainer = styled.div`
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: ${Space.sp3x};
`;

const TableHeader = styled.th<any>`
  padding: 12px;
  text-align: left;
  background-color: ${({ bgColor }) => bgColor || '#f9f9f9'};
  font-weight: bold;
  font-size: 14px;
  border-bottom: 1px solid #ddd;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableCell = styled.td<{ bold?: boolean }>`
  padding: 12px;
  border-bottom: 1px solid #ddd;
  font-weight: ${(props) => (props.bold ? FontWeight.bold : 'normal')};
  font-size: 14px;
`;

const OptionalAddonsHeader = styled.tr<any>`
  font-style: italic;
  background-color: ${({ bgColor }) => bgColor || '#f9f9f9'};
`;

const Heading = styled.h5`
  font-size: 20px;
  line-height: 24px;
  margin: ${Space.sp2x} 0;
`;

const UnderlinedButton = styled.button`
  background: none;
  border: none;
  color: ${Main.brandDark};
  text-decoration: underline;
  cursor: pointer;
  font-size: 14px;
  align-self: flex-start;
  margin-bottom: 20px;

  &:hover {
    text-decoration: none;
  }
`;

const CloseIcon = styled(Icon).attrs({ name: 'x', size: 20 })`
  color: #de5100;
`;

const TableRow = styled.tr`
`;


// Component for a single product row
const ProductRow: React.FC<{
    product: ProductAddonPreview;
    country: string | null;
    isMainProduct?: boolean;
    hasAddons?: boolean;
    onEditEntry?: (id: number, value: any) => void;
    onDeleteEntry?: (id: number) => void;
}> = ({
    product,
    country,
    isMainProduct = false,
    hasAddons = false,
    onEditEntry,
    onDeleteEntry,
}) => {
        const [snError, setSnError] = useState('');
        const [qtyError, setQtyError] = useState('');
        const [puError, setPuError] = useState('');

        const handeErrorUpdate = (error: string, key: string) => {
            let errorArray = product.error || [];
            if (!!error) {
                errorArray?.push(key);
            } else {
                errorArray = errorArray?.filter(x => x !== key)
            }

            return errorArray;
        }

        const handleSerialNumberChange = (e: any) => {
            const error = serialNumberValidator(e);
            setSnError(error);

            const errorArray = handeErrorUpdate(error, 'serialNumber');
            const value = {
                serialNumber: e,
                error: errorArray
            }

            onEditEntry?.(product.id, value);
        }

        const handleQuantityChange = (e: any) => {
            const error = productAddonQuantityValidator(e);
            setQtyError(error);

            const errorArray = handeErrorUpdate(error, 'quantity');
            const value = {
                quantity: parseInt(e),
                totalPrice: errorArray.length > 0 ? 0 : parseInt(e) * product.basePrice,
                error: errorArray
            }

            onEditEntry?.(product.id, value);
        }

        const handlePlannedUsageChange = (e: any) => {
            const error = productAddonPlannedUsageValidator(e)
            setPuError(error);

            const errorArray = handeErrorUpdate(error, 'plannedUsage');
            const value = {
                plannedUsage: e,
                totalPrice: errorArray.length > 0 ? 0 : parseInt(e) * product.basePrice,
                error: errorArray
            }

            onEditEntry?.(product.id, value);
        }

        return (
            <TableRow>
                {hasAddons && (
                    <TableCell>
                        {onDeleteEntry && (
                            <CloseIcon onClick={() => onDeleteEntry(product.id)} className="pointer" />
                        )}
                    </TableCell>
                )}
                <TableCell bold={isMainProduct}>
                    {!isMainProduct && <>&nbsp;&nbsp;&nbsp;•&nbsp;&nbsp;</>}{product.productName}
                </TableCell>
                {isMainProduct && onEditEntry ? (
                    <EditCell
                        value={product.serialNumber}
                        columnIndex={1}
                        error={!!snError}
                        errorMessage={snError}
                        onChange={(e, v) => setSnError('')}
                        onBlurHandler={(e) => handleSerialNumberChange(e)}
                        hideTooltip={false}
                        showEditIcon
                        editIconColor={Shade.shade340}
                        fontWeight={FontWeight.bold}
                        editIconMargin='5px 5px'
                        padding='0.25rem 0px 0.25rem 0.75rem'
                        emptyCell={false}
                    />
                ) : (
                    <TableCell bold={isMainProduct}>{product.serialNumber || 'N/A'}</TableCell>
                )}
                {!isMainProduct && onEditEntry ? (
                    <EditCell
                        value={product.quantity}
                        error={!!qtyError}
                        errorMessage={qtyError}
                        onChange={(e, v) => setQtyError('')}
                        onBlurHandler={(e) => handleQuantityChange(e)}
                        columnIndex={2}
                        showEditIcon
                        editIconColor={Shade.shade340}
                        editIconMargin='5px 5px'
                        type='number'
                        padding='0.25rem 0px 0.25rem 0.75rem'
                        hideTooltip={false}
                    />
                ) : (
                    <TableCell bold={isMainProduct}>{product.quantity}</TableCell>
                )}
                <TableCell bold={isMainProduct}>{product.unit.code}</TableCell>
                <TableCell bold={isMainProduct}>{currencySign(country, product.basePrice)}</TableCell>
                {isMainProduct && onEditEntry ? (
                    <EditCell
                        error={!!puError}
                        errorMessage={puError}
                        value={product.plannedUsage}
                        onChange={(e, v) => setPuError('')}
                        onBlurHandler={(e) => handlePlannedUsageChange(e)}
                        columnIndex={2}
                        showEditIcon
                        editIconColor={Shade.shade340}
                        fontWeight={FontWeight.bold}
                        editIconMargin='5px 5px'
                        type='number'
                        hideTooltip={false}
                        padding='0.25rem 0px 0.25rem 0.75rem'
                    />
                ) : (
                    <TableCell bold={isMainProduct}>{product.plannedUsage || 1}</TableCell>
                )}
                <TableCell bold={isMainProduct}>{currencySign(country, product.totalPrice)}</TableCell>
            </TableRow>
        );
    };


// Component for table headers - reused in both tables
const TableHeaderRow: React.FC<{ hasOptionalAddons?: boolean, backgroundColor?: string }> = ({ hasOptionalAddons = false, backgroundColor }) => (
    <TableRow>
        {hasOptionalAddons && <TableHeader bgColor={backgroundColor} />} {/* Empty header for CloseIcon alignment */}
        <TableHeader bgColor={backgroundColor}>Description</TableHeader>
        <TableHeader bgColor={backgroundColor}>S/N</TableHeader>
        <TableHeader bgColor={backgroundColor}>QTY</TableHeader>
        <TableHeader bgColor={backgroundColor}>Unit</TableHeader>
        <TableHeader bgColor={backgroundColor}>Unit Price</TableHeader>
        <TableHeader bgColor={backgroundColor}>Usage</TableHeader>
        <TableHeader bgColor={backgroundColor}>Total</TableHeader>
    </TableRow>
);

const OrderAddonTablePreview: React.FC<{
    products: ProductAddonPreview[];
    country?: string | null;
    title?: string;
    onAddEntry?: (val: ProductAddonPreview) => void;
    onEditEntry?: (id: number, value: any) => void;
    onDeleteEntry?: (id: number) => void;
    vendorId?: number;
    backgroundColor?: string;
    consignmentId?: string;
}> = ({ products, country, title, onAddEntry, onEditEntry, onDeleteEntry, vendorId, backgroundColor, consignmentId }) => {
    const { countries } = useCountries();

    const mainProduct = products.find((product) => product.parentEntryNumber === null);
    const mandatoryAddons = products.filter(
        (product) => product.requiredAddon && product.parentEntryNumber !== null
    );
    const optionalAddons = products.filter(
        (product) => !product.requiredAddon && product.parentEntryNumber !== null
    );

    const [showAddNewAddon, setShowAddNewAddon] = useState(false);
    const [productAddon, setProductAddon] = useState<IProductAddonForm>(emptyProductAddon);
    const [hasError, setHasError] = useState(false);

    const hasAddons = optionalAddons.length > 0 || mandatoryAddons.length > 0;

    const handleResetAddNewAddon = () => {
        setShowAddNewAddon?.(false);
        setProductAddon(emptyProductAddon);
    }

    const getProductAddonMappingReq = useAPI({
        deferFn: validateProductAddonMapping,
        onResolve: (result: any) => {
            if (result) {
                // map data and update the table
                const countryId = countries.find(x => x.countryIso === country)?.id;
                const servicePriceObject = result.service?.servicePrices?.find((x: any) => x.countryId === countryId);
                const mappedProductAddon: ProductAddonPreview = {
                    id: -1,
                    entryNumber: 0,
                    parentEntryNumber: result.entryNumber,
                    productId: '',
                    productSKU: result.service?.sku,
                    productName: result?.service.name,
                    quantity: productAddon.quantity,
                    basePrice: servicePriceObject?.pricePerUnit,
                    totalPrice: servicePriceObject?.pricePerUnit * productAddon.quantity,
                    plannedUsage: 0,
                    requiredAddon: !result.optional,
                    unit: {
                        id: servicePriceObject?.unitOfMeasurement?.id,
                        code: servicePriceObject?.unitOfMeasurement?.fullName
                    },
                    serialNumber: '',
                    addonId: result.service?.id,
                    error: []
                }

                onAddEntry?.(mappedProductAddon);
                setProductAddon(emptyProductAddon);
                setShowAddNewAddon?.(!showAddNewAddon)
            }
        },
        onReject: err => {
            setHasError(!!err.message)
        }
    });

    const getProductAddonMapping = () => {
        getProductAddonMappingReq.run(mainProduct?.id!, productAddon.sku, vendorId!, consignmentId!);
    }

    return (
        <>
            {
                title &&
                <Heading>{title}</Heading>
            }
            <TableContainer>
                <StyledTable>
                    <thead>
                        <TableHeaderRow hasOptionalAddons={hasAddons && !!onDeleteEntry} backgroundColor={backgroundColor} />
                    </thead>
                    <tbody>
                        {mainProduct && (
                            <ProductRow
                                product={mainProduct}
                                country={country!}
                                isMainProduct={true}
                                onEditEntry={onEditEntry}
                                hasAddons={hasAddons && !!onEditEntry}
                            />
                        )}

                        {mandatoryAddons.map((product, index) => (
                            <ProductRow
                                key={index}
                                product={product}
                                country={country!}
                                onEditEntry={onEditEntry}
                                onDeleteEntry={onDeleteEntry}
                                hasAddons={hasAddons && !!onEditEntry}
                            />
                        ))}

                        {optionalAddons.length > 0 && (
                            <>
                                <OptionalAddonsHeader bgColor={backgroundColor}>
                                    {hasAddons && onEditEntry && <TableCell />}
                                    <TableCell colSpan={hasAddons ? 8 : 7}>Optional add-ons</TableCell>
                                </OptionalAddonsHeader>
                                {optionalAddons.map((product, index) => (
                                    <ProductRow
                                        key={index}
                                        product={product}
                                        country={country!}
                                        onEditEntry={onEditEntry}
                                        onDeleteEntry={onDeleteEntry}
                                        hasAddons={hasAddons && !!onEditEntry}
                                    />
                                ))}
                            </>
                        )}
                    </tbody>
                </StyledTable>
            </TableContainer>
            {
                showAddNewAddon ?
                    <OrderAddonForm
                        productAddon={productAddon!}
                        setProductAddon={setProductAddon!}
                        onAdd={getProductAddonMapping}
                        hasError={hasError}
                        setHasError={setHasError}
                        isLoading={getProductAddonMappingReq.isLoading}
                        onCancel={handleResetAddNewAddon}
                    /> :
                    onAddEntry ?
                        <UnderlinedButton
                            onClick={() => setShowAddNewAddon?.(!showAddNewAddon)}
                        >
                            Add add-on
                        </UnderlinedButton> : <></>
            }
        </>
    );
};

export default OrderAddonTablePreview;